/**
 * @file This file contains configuration for various temporary visual effects in the game.
 * @module temporaryEffectsConfig
 */

/**
 * @typedef {Object} TemporaryEffectConfig
 * @property {string} display - The emoji or character used to display the effect.
 * @property {number} duration - The duration of the effect in milliseconds.
 * @property {boolean} [isGlobal] - Optional. Indicates if the effect affects the whole screen rather than a single cell.
 */

/**
 * Configuration object for different temporary effects.
 * Each key represents a unique effect.
 * @type {Object.<string, TemporaryEffectConfig>}
 */
export const temporaryEffectsConfig = {
    'cleaned': {
        display: ['✨', '💫', '💖'], // Sparkles, Dizzy, Sparkling Heart
        duration: 1000 // 1 second
    },
    'dust': {
        display: ['💨', '🍃', '🍂'], // Puff of smoke, Leaf fluttering, Fallen leaf
        duration: 1500
    },
    'godzilla': {
        display: ['🦖', '🐉', '🐲'], // T-Rex, Dragon, Dragon Face
        duration: 3000,
        isGlobal: true // Indicates it affects the whole screen, not just a cell
    },
    'megaMerge': {
        display: ['🌟', '💥', '💫'], // Glowing Star, Explosion, Dizzy
        duration: 1000,
        className: 'mega-merge-effect'
    },
    'achievementUnlock': {
        display: ['🏆', '🎉', '🏅'], // Trophy, Party Popper, Medal
        duration: 2000, // 2 seconds
        className: 'achievement-unlock-effect'
    },
    'npcChat': {
        display: ['💬', '💭','🗨️','🗯️'], // Speech bubble, Thought bubble, Speaking head
        duration: 1500, // 1.5 seconds
        className: 'npc-chat-effect',
        delay: 500 // 500ms delay to sync with 0.5s NPC movement animation
    },
    'npcTarget-disabled-this-one': {
        display: '📍', // Signpost emoji
        className: 'npc-target-effect',
        // No duration, this effect is cleared manually
    },
    'eating': {
        display: ['😋', '🍴', '🍽️'], // Yummy face, fork and knife
        duration: 1000, // 1 second
        className: 'eating-effect' // For custom styling if needed
    },
    'playing': {
        display: ['⚽', '🏀', '🎾'], // Sports balls
        duration: 1500,
        className: 'playing-effect'
    },
    'love': {
        display: '❤️',
        duration: 2000,
        className: 'love-effect'
    },
    'play_fight': {
        display: '⚔️',
        duration: 2000,
        className: 'play-fight-effect'
    },
    'kiss': {
        display: '💋',
        duration: 1500,
        className: 'kiss-effect'
    },
    'drinking': {
        display: ['🥂', '🍻', '🥃'],
        duration: 2000,
        className: 'drinking-effect'
    },
    'glowing': {
        display: ['✨', '🌟'],
        duration: 2500,
        className: 'glowing-effect'
    },
    'music_notes': {
        display: ['🎵', '🎶', '🎼'],
        duration: 3000,
        className: 'music-notes-effect'
    },
    'working': {
        display: ['🛠️', '⚙️', '🔩'],
        duration: 2000,
        className: 'working-effect'
    },
    'reading': {
        display: ['🧐', '🤔', '📜'],
        duration: 2500,
        className: 'reading-effect'
    },
    'toy_play': {
        display: ['🧸', '🪁', '🧩'],
        duration: 2000,
        className: 'toy-play-effect'
    },
    'shining_light': {
        display: ['💡', '✨'],
        duration: 2000,
        className: 'shining-light-effect'
    },
    'waving_flag': {
        display: ['🚩', '🎌'],
        duration: 3000,
        className: 'waving-flag-effect'
    },
    'dancing': {
        display: ['💃', '🕺'],
        duration: 3000,
        className: 'npc-action-effect'
    },
    'waving_hand': {
        display: '👋',
        duration: 2000,
        className: 'npc-action-effect'
    },
    'sleeping': {
        display: '💤',
        duration: 5000,
        className: 'npc-action-effect'
    },
    'zany_thought': {
        display: ['😵', '🤯', '🤪', '🤔', '🧐', '⁉️'],
        duration: 2500,
        className: 'zany-thought-effect'
    }
};
